import type { HelpGuide } from '../types';

export const proptaxGuide: HelpGuide = {
  key: 'proptax',
  title: 'ภาษีที่ดิน & ป้าย (Property Tax)',
  subtitle: 'เมนู: รายงานและภาษี ▸ ภาษีที่ดิน & ป้าย',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/proptax/คู่มือการใช้งาน_ภาษีที่ดินและป้าย_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ภาษีที่ดินและสิ่งปลูกสร้าง + ภาษีป้าย — ทะเบียนรายปี” ใช้คุมภาษีท้องถิ่นที่บริษัทต้องจ่ายเป็นรายปีของแต่ละทรัพย์สิน/สาขา ได้แก่ ภาษีที่ดินและสิ่งปลูกสร้าง (จ่ายภายใน 30 เม.ย.) และ ภาษีป้าย (จ่ายภายใน 31 มี.ค.) โดยตั้งประมาณการเต็มปีไว้ล่วงหน้า แล้วบันทึกการจ่ายจริงเมื่อถึงกำหนด ระบบจะออกเลขที่ใบสำคัญและโพสต์ใบสำคัญ (JV) ให้อัตโนมัติทั้งตอนตั้งประมาณการและตอนจ่าย',
        },
        {
          kind: 'callout',
          text: 'หลักการบันทึกบัญชี: ตั้งประมาณการ → Dr. ค่าใช้จ่ายภาษี (60110114 ที่ดิน / 60110011 ป้าย) / Cr. ค่าใช้จ่ายค้างจ่าย (20170036 ที่ดิน / 20170035 ป้าย) เต็มจำนวนทั้งปี · จ่ายจริง → Dr. ค่าใช้จ่ายค้างจ่าย (20170036 / 20170035) / Cr. เงินฝากธนาคาร (บัญชีหลักของบริษัท) · รายเดือนแสดงการตัดจ่าย 1/12 ใน Matrix เชิงข้อมูล (ยังไม่มี JV รายเดือนในเวอร์ชันนี้)',
        },
        { kind: 'para', text: 'หน้าจอมี 3 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'รายการประมาณการ — ',
              text: 'ตารางรายการภาษีทั้งปี + ตัวกรอง + ปุ่มบันทึกจ่าย (แท็บเริ่มต้น)',
            },
            {
              lead: 'ตารางตัดจ่ายรายเดือน (Matrix) — ',
              text: 'ทรัพย์สิน × 12 เดือน แสดงยอด 1/12 ต่อเดือน พร้อมไฮไลต์เดือนที่จ่ายจริง',
            },
            {
              lead: 'สรุปตามทรัพย์สิน — ',
              text: 'การ์ดสรุปแยกตามประเภทภาษี + ตารางสรุปตามสาขา',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/proptax/01-overview.png',
          caption:
            'ภาพรวมทะเบียนภาษีที่ดินและป้าย — KPI 4 ใบ, แผงอธิบายขั้นตอนการบันทึกบัญชี และตารางรายการประมาณการทั้งปี',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบด้านบน:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'รายการในทะเบียน — ', text: 'จำนวนรายการภาษีในปีที่เลือก (ที่ดิน + ป้าย)' },
            {
              lead: 'ภาษีรวมปี {ปี} — ',
              text: 'ยอดภาษีต้องจ่ายรวมทั้งปี (ตามมูลค่าประเมินทั้งหมด)',
            },
            {
              lead: 'จ่ายจริงไปแล้ว — ',
              text: 'ยอดที่จ่ายจริงไปแล้ว พร้อมจำนวนรายการที่จ่ายแล้ว/ทั้งหมด',
            },
            {
              lead: 'เกินกำหนด — ',
              text: 'จำนวนรายการที่เลยกำหนดจ่ายแต่ยังไม่จ่าย (ต้องเร่งดำเนินการ)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ใต้ KPI มีแผงอธิบาย 3 ขั้นตอน: (1) ลงทะเบียน — ตั้งประมาณการเต็มปี (Dr. 60110114/60110011 / Cr. 20170036/20170035) (2) จ่ายจริง — เคลียร์ค่าใช้จ่ายค้างจ่าย (Dr. 20170036/20170035 / Cr. เงินฝากธนาคาร บัญชีหลักของบริษัท) (3) รายเดือน — Matrix แสดงการตัดจ่าย 1/12 (เชิงข้อมูล) ปุ่มมุมขวาบน: เลือก ปีภาษี (2024–2027) · ส่งออก · + ตั้งประมาณการใหม่ (ปุ่มหลัก)',
        },
      ],
    },
    {
      id: 'items',
      heading: '3. แท็บรายการประมาณการ',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ รายการประมาณการ แสดงรายการภาษีทั้งปีในตารางเดียว กรองด้วย: ช่องค้นหา (ค้นหา เลขที่ / สถานที่) · ประเภทภาษี (ภาษีที่ดินและสิ่งปลูกสร้าง / ภาษีป้าย) · สถานะ · สาขา · หน่วยงาน',
        },
        {
          kind: 'para',
          text: 'คอลัมน์ในตาราง: เลขที่ · สถานที่ / ทรัพย์สิน · ประเภทภาษี · มูลค่าประเมิน · อัตราภาษี · ภาษีต้องจ่าย · กำหนดจ่าย · วันจ่ายจริง · สถานะ · การกระทำ แถวล่างสุดสรุป “รวม N รายการ” พร้อมยอดภาษีรวม แถวที่ยังไม่จ่าย (รอจ่าย/เกินกำหนด) จะมีปุ่ม บันทึกจ่าย → ที่คอลัมน์การกระทำ',
        },
        {
          kind: 'image',
          src: '/help/proptax/02-items.png',
          caption:
            'ตารางทะเบียนรายการภาษีทั้งปี — แสดงประเภทภาษี, มูลค่าประเมิน, ภาษีต้องจ่าย, กำหนดจ่าย, สถานะ และปุ่มบันทึกจ่าย',
        },
      ],
    },
    {
      id: 'overdue',
      heading: '4. การกรองรายการที่เกินกำหนดชำระ',
      blocks: [
        {
          kind: 'para',
          text: 'เลือกตัวกรอง สถานะ เป็น เกินกำหนด เพื่อดูเฉพาะรายการที่เลยวันกำหนดจ่ายแล้วแต่ยังไม่ได้จ่าย รายการเหล่านี้จะแสดงป้ายสถานะ “เกินกำหนด” (สีแดง/โทนเตือน) และนับรวมในการ์ด KPI “เกินกำหนด” ใช้เพื่อตรวจรายการที่ต้องเร่งจ่ายเพื่อเลี่ยงเบี้ยปรับ',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'สถานะ “เกินกำหนด” เป็นค่าที่ระบบคำนวณให้เอง (จากกำหนดจ่ายเทียบกับวันนี้) ไม่ได้เก็บไว้ในฐานข้อมูล เมื่อบันทึกจ่ายแล้วสถานะจะเปลี่ยนเป็น “จ่ายแล้ว” โดยอัตโนมัติ',
        },
        {
          kind: 'image',
          src: '/help/proptax/03-filter-overdue.png',
          caption: 'กรองเฉพาะรายการที่เกินกำหนดชำระ — ป้ายสถานะ “เกินกำหนด” และ KPI เกินกำหนด',
        },
      ],
    },
    {
      id: 'matrix',
      heading: '5. แท็บตารางตัดจ่ายรายเดือน (Matrix)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ตารางตัดจ่ายรายเดือน (Matrix) แสดงทรัพย์สินแต่ละรายการเทียบกับ 12 เดือน (ม.ค.–ธ.ค.) โดยกระจายยอดภาษีต้องจ่ายเป็น 1/12 ต่อเดือน คอลัมน์: สถานที่ · ประเภท · ภาษีต้องจ่าย · ม.ค. ถึง ธ.ค. แถวล่างสุดสรุป “รวมทุกทรัพย์สิน”',
        },
        {
          kind: 'para',
          text: 'สีในตาราง: ช่องสีฟ้าอ่อน = ตัดจ่ายปกติ · ช่องสีส้ม (ขอบหนา) = เดือนที่จ่ายจริง ตารางนี้เป็นมุมมองเชิงข้อมูลเพื่อดูการกระจายภาษีตลอดปี ยังไม่มีการลงใบสำคัญ (JV) รายเดือนในเวอร์ชันนี้',
        },
        {
          kind: 'image',
          src: '/help/proptax/04-matrix.png',
          caption:
            'ตารางตัดจ่ายรายเดือน (Matrix) ทรัพย์สิน × 12 เดือน — ยอด 1/12 ต่อเดือน ไฮไลต์เดือนที่จ่ายจริงด้วยสีส้ม',
        },
      ],
    },
    {
      id: 'summary',
      heading: '6. แท็บสรุปตามทรัพย์สิน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ สรุปตามทรัพย์สิน มีการ์ดสรุปแยกตามประเภทภาษี 2 ใบ (ภาษีที่ดินและสิ่งปลูกสร้าง / ภาษีป้าย) แต่ละการ์ดแสดง: ภาษีรวมทั้งปี · จ่ายแล้ว (พร้อม %) · ค้างจ่าย · ตัดจ่าย/เดือน (1/12) พร้อมเลขบัญชีและจำนวนทรัพย์สินของประเภทนั้น',
        },
        {
          kind: 'para',
          text: 'ด้านล่างมีตาราง สรุปตามสาขา แสดงต่อสาขา: จำนวนรายการ · ภาษีรวม · จ่ายแล้ว · ค้างจ่าย (รายการที่ไม่ระบุสาขาจะรวมในแถว “— ไม่ระบุสาขา —”) ใช้ดูภาระภาษีของแต่ละสาขาในภาพรวม',
        },
        {
          kind: 'image',
          src: '/help/proptax/05-summary.png',
          caption:
            'สรุปตามทรัพย์สิน — การ์ดแยกประเภทภาษี (รวม/จ่ายแล้ว/ค้างจ่าย/ตัดจ่ายต่อเดือน) และตารางสรุปตามสาขา',
        },
      ],
    },
    {
      id: 'create',
      heading: '7. การตั้งประมาณการภาษีใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม + ตั้งประมาณการใหม่ จะเปิดฟอร์ม “ตั้งประมาณการภาษีรายการใหม่ ปี {ปี}” กรอก: ประเภทภาษี (เลือกการ์ด ที่ดิน/ป้าย — ระบบตั้งกำหนดจ่ายให้ตามประเภท) · สาขา · หน่วยงาน · สถานที่ / ทรัพย์สิน · มูลค่าประเมิน · อัตราภาษี (%) · ภาษีต้องจ่าย · กำหนดจ่าย · หมายเหตุ (ไม่บังคับ)',
        },
        {
          kind: 'para',
          text: 'เมื่อกด สร้าง + โพสต์ JV accrual ระบบจะออกเลขที่ใบสำคัญรูปแบบ PT-YYYY-NNNNNN และโพสต์ใบสำคัญตั้งค้างจ่ายเต็มจำนวนให้อัตโนมัติ (Dr. 60110114 ที่ดิน หรือ 60110011 ป้าย / Cr. 20170036 ที่ดิน หรือ 20170035 ป้าย) ปุ่มจะกดได้ต่อเมื่อระบุสถานที่และจำนวนภาษีมากกว่า 0',
        },
        {
          kind: 'image',
          src: '/help/proptax/06-create.png',
          caption:
            'ตั้งประมาณการใหม่ — เลือกประเภทภาษี, กรอกมูลค่าประเมิน/อัตรา/ภาษี แล้วโพสต์ JV accrual อัตโนมัติ',
        },
      ],
    },
    {
      id: 'pay',
      heading: '8. การบันทึกการจ่ายภาษี',
      blocks: [
        {
          kind: 'para',
          text: 'ที่แท็บรายการประมาณการ กดปุ่ม บันทึกจ่าย → บนแถวที่ยังไม่จ่าย (รอจ่าย/เกินกำหนด) จะเปิดฟอร์ม “บันทึกการจ่าย” ที่แสดงเลขที่ · สถานที่ · ยอดภาษีไว้ให้แล้ว กรอก: วันที่จ่าย (ค่าเริ่มต้น = วันนี้) · บัญชีเงินสด/ธนาคาร (Cr.) (เว้นว่างได้ = ใช้บัญชีธนาคารหลักตามที่ตั้งไว้ในเมนู ตั้งค่าบัญชีอ้างอิง)',
        },
        {
          kind: 'para',
          text: 'เมื่อกด ยืนยัน & สร้าง JV จ่าย ระบบจะโพสต์ใบสำคัญจ่าย (Dr. 20170036/20170035 ค่าใช้จ่ายค้างจ่าย / Cr. เงินสด/ธนาคารตามบัญชีที่เลือก) แล้วเปลี่ยนสถานะรายการเป็น “จ่ายแล้ว” พร้อมบันทึกวันจ่ายจริง',
        },
        {
          kind: 'image',
          src: '/help/proptax/07-pay.png',
          caption:
            'บันทึกการจ่าย — ระบุวันที่จ่ายและบัญชีเงินสด/ธนาคาร พร้อมตัวอย่างใบสำคัญ Dr. 20170036/20170035 / Cr. เงินฝากธนาคารหลัก (ตามตั้งค่า)',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '9. สรุปสถานะของรายการภาษี',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['รอจ่าย', 'ตั้งประมาณการแล้ว ยังไม่ถึง/ยังไม่เลยกำหนดจ่าย — กดบันทึกจ่ายได้'],
            ['เกินกำหนด', 'เลยวันกำหนดจ่ายแล้วแต่ยังไม่จ่าย (ระบบคำนวณให้) — ต้องเร่งจ่าย'],
            ['จ่ายแล้ว', 'บันทึกการจ่ายและลง JV จ่ายแล้ว (สถานะสิ้นสุด)'],
            ['ยกเลิก', 'รายการถูกยกเลิก (สถานะสิ้นสุด)'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '10. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ตั้งประมาณการแล้วต้องลง JV เองไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่ต้อง — ระบบโพสต์ใบสำคัญตั้งค้างจ่าย (Dr. 60110114/60110011 / Cr. 20170036/20170035) ให้อัตโนมัติเมื่อกดสร้าง',
            },
          ],
        },
        { kind: 'para', text: 'ทำไมบางแถวไม่มีปุ่มบันทึกจ่าย?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ปุ่มบันทึกจ่ายจะแสดงเฉพาะแถวสถานะ “รอจ่าย” หรือ “เกินกำหนด” เท่านั้น รายการที่จ่ายแล้ว/ยกเลิกจะไม่มีปุ่ม',
            },
          ],
        },
        { kind: 'para', text: 'Matrix รายเดือนลง JV ทุกเดือนไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่ — Matrix เป็นมุมมองเชิงข้อมูล (แสดง 1/12 ต่อเดือน) เวอร์ชันนี้ยังไม่มีการลง JV รายเดือน',
            },
          ],
        },
        { kind: 'para', text: 'กำหนดจ่ายของแต่ละประเภทคือเมื่อไหร่?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ภาษีที่ดินและสิ่งปลูกสร้าง: ภายใน 30 เม.ย. · ภาษีป้าย: ภายใน 31 มี.ค. ของทุกปี (ระบบตั้งกำหนดจ่ายให้ตามประเภทที่เลือก)',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '11. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ภาษีที่ดินและสิ่งปลูกสร้าง — ',
              text: 'ภาษีท้องถิ่นตาม พรบ. ภาษีที่ดินและสิ่งปลูกสร้าง 2562 จ่ายภายใน 30 เม.ย. (บัญชี 60110114)',
            },
            {
              lead: 'ภาษีป้าย — ',
              text: 'ภาษีป้ายโฆษณา จ่ายภายใน 31 มี.ค. ของทุกปี (บัญชี 60110011)',
            },
            {
              lead: 'มูลค่าประเมิน — ',
              text: 'มูลค่าที่ใช้เป็นฐานคำนวณภาษี (ราคาประเมินที่ดิน/สิ่งปลูกสร้าง หรือพื้นที่ป้าย)',
            },
            {
              lead: 'ตั้งประมาณการ (Accrual) — ',
              text: 'การตั้งค่าใช้จ่ายภาษีค้างจ่ายเต็มปีไว้ล่วงหน้า (Dr. ค่าใช้จ่าย / Cr. 20170036 ที่ดิน / 20170035 ป้าย)',
            },
            {
              lead: 'ค่าใช้จ่ายค้างจ่าย (20170036 ที่ดิน / 20170035 ป้าย) — ',
              text: 'บัญชีหนี้สินที่พักภาษีที่ตั้งไว้ รอเคลียร์เมื่อจ่ายจริง',
            },
            {
              lead: 'ตัดจ่าย 1/12 — ',
              text: 'การกระจายภาษีทั้งปีออกเป็นรายเดือน (1 ใน 12) เพื่อดูภาพการตัดจ่าย (เชิงข้อมูล)',
            },
            {
              lead: 'PT-YYYY-NNNNNN — ',
              text: 'รูปแบบเลขที่ใบสำคัญของรายการภาษีที่ระบบออกให้อัตโนมัติเมื่อตั้งประมาณการ',
            },
          ],
        },
      ],
    },
  ],
};
